// src/components/TemplatePicker.jsx
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { sendTemplate } from '../services/templateService';

function extractVariables(content) {
  const bodyRegex = /\{\{(\d+)\}\}/g;
  const bodyVariables = new Set();
  let match;
  while ((match = bodyRegex.exec(content)) !== null) {
    bodyVariables.add(match[1]);
  }
  const buttonRegex = /\[button:(\d+)\]/g;
  const buttonVariables = new Set();
  while ((match = buttonRegex.exec(content)) !== null) {
    buttonVariables.add(match[1]);
  }
  return {
    body: Array.from(bodyVariables).sort((a, b) => Number(a) - Number(b)),
    buttons: Array.from(buttonVariables).sort((a, b) => Number(a) - Number(b))
  };
}

export default function TemplatePicker({ conversationId, disabled = false, templates = [], loading = false }) {
  const { t } = useTranslation();

  const [listOpen, setListOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Send modal state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [bodyVariableValues, setBodyVariableValues] = useState({});
  const [buttonVariableValues, setButtonVariableValues] = useState({});

  const handleOpen = () => {
    if (disabled) return;
    setSearch('');
    setListOpen(true);
  };

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter(t =>
      t.name.toLowerCase().includes(q) || t.content.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const handleTemplateClick = (template) => {
    setListOpen(false);
    setSelectedTemplate(template);
    setBodyVariableValues({});
    setButtonVariableValues({});
    setError('');
    setPreviewOpen(true);
  };

  // Template variables and preview
  const templateVariables = useMemo(() => {
    if (!selectedTemplate) return { body: [], buttons: [] };
    return extractVariables(selectedTemplate.content);
  }, [selectedTemplate]);

  const previewContent = useMemo(() => {
    if (!selectedTemplate) return '';
    let content = selectedTemplate.content;
    templateVariables.body.forEach(varNum => {
      const value = bodyVariableValues[varNum] || `{{${varNum}}}`;
      content = content.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), value);
    });
    templateVariables.buttons.forEach(varNum => {
      const value = buttonVariableValues[varNum] || `[button:${varNum}]`;
      content = content.replace(new RegExp(`\\[button:${varNum}\\]`, 'g'), `[BUTTON: ${value}]`);
    });
    return content;
  }, [selectedTemplate, templateVariables, bodyVariableValues, buttonVariableValues]);

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;
    const missingBodyVars = templateVariables.body.filter(varNum => !bodyVariableValues[varNum]?.trim());
    if (missingBodyVars.length > 0) {
      setError(`Faltan variables del mensaje: ${missingBodyVars.join(', ')}`);
      return;
    }
    const missingButtonVars = templateVariables.buttons.filter(varNum => !buttonVariableValues[varNum]?.trim());
    if (missingButtonVars.length > 0) {
      setError(`Faltan variables de botones: ${missingButtonVars.join(', ')}`);
      return;
    }
    try {
      setSending(true);
      setError('');
      await sendTemplate(conversationId, selectedTemplate.id, {
        body: bodyVariableValues,
        buttons: buttonVariableValues
      });
      setPreviewOpen(false);
      setSelectedTemplate(null);
      setBodyVariableValues({});
      setButtonVariableValues({});
    } catch (err) {
      console.error('[TemplatePicker] Error sending template:', err);
      setError(t('templates.error.send') || 'Error enviando plantilla');
    } finally {
      setSending(false);
    }
  };

  // List modal
  const listModal = listOpen && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={() => setListOpen(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 relative max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setListOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl z-10"
        >
          ✕
        </button>

        <h2 className="text-xl font-semibold mb-4">
          {t('templates.title') || 'Plantillas'}
        </h2>

        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('templates.search') || 'Buscar plantilla...'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          autoFocus
        />

        <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
          {loading ? (
            <p className="text-center py-4 text-gray-500">
              {t('common.loading') || 'Cargando...'}
            </p>
          ) : filteredTemplates.length === 0 ? (
            <p className="text-center py-4 text-gray-500">
              {search ? (t('templates.noResults') || 'No se encontraron plantillas') : (t('templates.empty') || 'No hay plantillas disponibles')}
            </p>
          ) : (
            filteredTemplates.map(template => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template)}
                className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 cursor-pointer transition-colors"
              >
                <div className="text-sm font-medium text-gray-800">
                  {template.name}
                </div>
                <div className="text-xs text-gray-500 truncate mt-1">
                  {template.content.substring(0, 80)}...
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  // Send/preview modal (same as TemplateSelector)
  const previewModal = previewOpen && selectedTemplate && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={() => setPreviewOpen(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setPreviewOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl z-10"
        >
          ✕
        </button>

        <h2 className="text-2xl font-semibold mb-6">
          {t('templates.preview.title') || 'Vista previa de plantilla'}
        </h2>

        <div className="flex gap-6">
          {(templateVariables.body.length > 0 || templateVariables.buttons.length > 0) && (
            <div className="w-1/3 border-r pr-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                {t('templates.variables.title') || 'Variables'}
              </h3>
              <div className="space-y-6">
                {templateVariables.body.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-3">
                      Variables del mensaje
                    </h4>
                    <div className="space-y-3">
                      {templateVariables.body.map(varNum => (
                        <div key={`body-${varNum}`}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Variable {varNum}
                          </label>
                          <input
                            type="text"
                            value={bodyVariableValues[varNum] || ''}
                            onChange={(e) => setBodyVariableValues(prev => ({ ...prev, [varNum]: e.target.value }))}
                            placeholder={`Valor para {{${varNum}}}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {templateVariables.buttons.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-3">
                      Variables de botones
                    </h4>
                    <div className="space-y-3">
                      {templateVariables.buttons.map(varNum => (
                        <div key={`button-${varNum}`}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Boton {varNum}
                          </label>
                          <input
                            type="text"
                            value={buttonVariableValues[varNum] || ''}
                            onChange={(e) => setButtonVariableValues(prev => ({ ...prev, [varNum]: e.target.value }))}
                            placeholder={`Valor para boton ${varNum}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Este valor se usara en el URL del boton
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={(templateVariables.body.length > 0 || templateVariables.buttons.length > 0) ? 'w-2/3' : 'w-full'}>
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">
                {t('templates.preview.name') || 'Nombre'}
              </h3>
              <p className="text-lg font-medium">{selectedTemplate.name}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">
                {t('templates.preview.content') || 'Vista previa'}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 min-h-[200px]">
                <p className="whitespace-pre-wrap">{previewContent}</p>
              </div>
              {(templateVariables.body.length > 0 || templateVariables.buttons.length > 0) && (
                <p className="text-xs text-gray-500 mt-2">
                  {t('templates.preview.hint') || 'Completa las variables en el panel izquierdo para ver el mensaje final'}
                </p>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPreviewOpen(false)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                disabled={sending}
              >
                {t('common.cancel') || 'Cancelar'}
              </button>
              <button
                onClick={handleSendTemplate}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                disabled={sending}
              >
                {sending ? (t('templates.sending') || 'Enviando...') : (t('templates.send') || 'Enviar plantilla')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded transition-colors text-sm ${
          disabled
            ? 'text-gray-400 cursor-not-allowed opacity-50'
            : 'cursor-pointer text-gray-600 hover:text-blue-600 hover:bg-blue-50'
        }`}
        title={disabled ? 'No puedes enviar plantillas' : (t('templates.title') || 'Plantillas')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
        {t('templates.title') || 'Plantillas'}
      </button>
      {listModal}
      {previewModal}
    </>
  );
}
